import { Fragment, useCallback, useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Eye,
  EyeOff,
  Mail,
  Send,
} from 'lucide-react'

import IntakeMailSettings from '@/components/settings/IntakeMailSettings'
import NewsSignOffPanel from '@/components/settings/NewsSignOffPanel'
import { newsSignOffSummary } from '@/components/settings/newsSignOff'
import { mapsConfigService, type MapsProbe } from '@/services/mapsConfig'
import { probeBrowserMaps, type BrowserMapsCheck } from '@/services/browserPlaces'
import api from '@/services/api'

const PERMANENT_SUPER_ADMIN_EMAIL = 'edward@helios-fintech.com'

export type IntegrationId =
  | 'helios-ai'
  | 'maps-browser'
  | 'maps-server'
  | 'fbi-crime'
  | 'census'
  | 'regrid'
  | 'serper'
  | 'smtp'
  | 'originations-mail'
  | 'costar'
  | 'reis'
  | 'corelogic'
  | 'bloomberg'
  | 'rates-public'
  | 'inbound-email'
  | 'report-receipts'

type IntegrationKind = 'Agent' | 'API key' | 'Email' | 'Mail' | 'Market data' | 'Webhook'

type RowStatus = 'configured' | 'not_configured' | 'blocked' | 'collecting' | 'available' | 'unknown'

type ProviderStatus = {
  provider: string
  display_name?: string
  configured?: boolean
  key_source?: string
  active_for?: string[]
}

type AiStatus = {
  claude_configured: boolean
  key_source?: string
  key_configured?: boolean
  blocked_reason?: string | null
}

type IntakeStatus = {
  mailboxActive: boolean
  mailbox: string | null
  mailboxConfigured: boolean
  collecting: boolean
}

type HubRow = {
  id: IntegrationId
  name: string
  kind: IntegrationKind
  summary: string
  status: RowStatus
  note: string
}

function FormLabel({ children }: { children: React.ReactNode }) {
  return <label className="mb-1 block text-[11px] font-medium text-stone-400">{children}</label>
}

function FormInput({
  type = 'text',
  value,
  onChange,
  placeholder = '',
  disabled = false,
}: {
  type?: string
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  disabled?: boolean
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className="input w-full text-[12px] disabled:opacity-40"
    />
  )
}

function FormSelect({
  options,
  value,
  onChange,
}: {
  options: readonly string[]
  value: string
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className="input w-full appearance-none pr-8 text-[12px]"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <ChevronDown size={12} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-stone-500" />
    </div>
  )
}

function StatusBadge({ status }: { status: RowStatus }) {
  const map: Record<RowStatus, { cls: string; dot: string; label: string }> = {
    configured: {
      cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
      dot: 'bg-emerald-400',
      label: 'Configured',
    },
    collecting: {
      cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
      dot: 'bg-emerald-400',
      label: 'Collecting',
    },
    available: {
      cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
      dot: 'bg-emerald-400',
      label: 'Available',
    },
    blocked: {
      cls: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
      dot: 'bg-amber-400',
      label: 'Blocked',
    },
    not_configured: {
      cls: 'bg-stone-500/15 text-stone-400 border-stone-500/20',
      dot: 'bg-stone-400',
      label: 'Not configured',
    },
    unknown: {
      cls: 'bg-stone-500/15 text-stone-400 border-stone-500/20',
      dot: 'bg-stone-400',
      label: 'Status unknown',
    },
  }
  const visual = map[status]
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${visual.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${visual.dot}`} />
      {visual.label}
    </span>
  )
}

function Flash({ ok, err }: { ok: string | null; err: string | null }) {
  if (err) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
        <AlertCircle size={14} /> {err}
      </div>
    )
  }
  if (ok) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-300">
        <CheckCircle2 size={14} /> {ok}
      </div>
    )
  }
  return null
}

function SecretField({
  label,
  value,
  onChange,
  configured,
  placeholder,
  envName,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  configured: boolean
  placeholder: string
  envName?: string
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="max-w-lg space-y-1.5">
      <FormLabel>{label}</FormLabel>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={configured ? '••••••••••••••••••••  (saved — enter new to replace)' : placeholder}
          autoComplete="new-password"
          className="input w-full pr-10 text-[12px] font-mono"
        />
        <button
          type="button"
          onClick={() => setShow((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
          aria-label={show ? `Hide ${label}` : `Show ${label}`}
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
      {envName ? (
        <p className="text-[10px] text-stone-600">
          Environment override: <span className="font-mono text-stone-500">{envName}</span>
        </p>
      ) : null}
    </div>
  )
}

function configuredStatus(on: boolean): RowStatus {
  return on ? 'configured' : 'not_configured'
}

function providerNote(status: ProviderStatus | undefined, fallback: string): string {
  if (!status) return fallback
  if (status.key_source === 'environment') return 'Configured by environment variable on the API host.'
  if (status.key_source === 'database') return 'Configured from saved settings.'
  if (status.key_source === 'public') return 'Public endpoints; no credentials.'
  if (status.configured) return 'Configured.'
  return fallback
}

export default function IntegrationsHub({ sessionEmail }: { sessionEmail: string }) {
  const queryClient = useQueryClient()
  const isPermanentSuperAdmin = sessionEmail.trim().toLowerCase() === PERMANENT_SUPER_ADMIN_EMAIL

  const [openId, setOpenId] = useState<IntegrationId | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [platform, setPlatform] = useState<Record<string, unknown>>({})
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null)
  const [providers, setProviders] = useState<ProviderStatus[]>([])
  const [intake, setIntake] = useState<IntakeStatus | null>(null)

  const [anthropicApiKey, setAnthropicApiKey] = useState('')
  const [aiModel, setAiModel] = useState('claude-sonnet-4-20250514')
  const [aiContextWindow, setAiContextWindow] = useState('128K tokens')
  const [aiTempPct, setAiTempPct] = useState(20)
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('')
  const [googleMapsServerApiKey, setGoogleMapsServerApiKey] = useState('')
  const [fbiCrimeApiKey, setFbiCrimeApiKey] = useState('')
  const [censusApiKey, setCensusApiKey] = useState('')
  const [regridApiKey, setRegridApiKey] = useState('')
  const [serperApiKey, setSerperApiKey] = useState('')
  const [costarApiKey, setCostarApiKey] = useState('')
  const [reisApiKey, setReisApiKey] = useState('')
  const [bloombergApiKey, setBloombergApiKey] = useState('')
  const [corelogicClientId, setCorelogicClientId] = useState('')
  const [corelogicClientSecret, setCorelogicClientSecret] = useState('')

  // Empty until the server answers: its DEFAULT_SMTP_SERVER / DEFAULT_SENDER_ADDRESS
  // settings (or this workspace's saved values) are what the form shows — this file
  // names no mail provider or sender of its own.
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState('587')
  const [smtpFrom, setSmtpFrom] = useState('')
  const [smtpUser, setSmtpUser] = useState('')
  const [smtpPass, setSmtpPass] = useState('')
  const [smtpStartTls, setSmtpStartTls] = useState(true)
  const [smtpSsl, setSmtpSsl] = useState(false)
  const [smtpPasswordConfigured, setSmtpPasswordConfigured] = useState(false)
  const [smtpManagedByEnvironment, setSmtpManagedByEnvironment] = useState(false)
  const [testTo, setTestTo] = useState(sessionEmail)

  const [mapsProbe, setMapsProbe] = useState<MapsProbe | null>(null)
  const [mapsProbing, setMapsProbing] = useState(false)
  const [browserChecks, setBrowserChecks] = useState<BrowserMapsCheck[] | null>(null)
  const [browserProbing, setBrowserProbing] = useState(false)

  const [savingId, setSavingId] = useState<IntegrationId | null>(null)
  const [flashOk, setFlashOk] = useState<string | null>(null)
  const [flashErr, setFlashErr] = useState<string | null>(null)
  const [smtpTestSending, setSmtpTestSending] = useState(false)

  const applyPlatform = useCallback((d: Record<string, unknown>) => {
    setPlatform(d)
    setAiModel(String(d.aiModel ?? 'claude-sonnet-4-20250514'))
    setAiContextWindow(String(d.aiContextWindow ?? '128K tokens'))
    const temperature = Number(d.aiTemperature)
    setAiTempPct(Number.isFinite(temperature) ? Math.round(temperature * 100) : 20)
    setSmtpHost(String(d.smtpServer ?? ''))
    setSmtpPort(String(d.smtpPort ?? '587'))
    setSmtpFrom(String(d.senderAddress ?? ''))
    setSmtpUser(String(d.smtpUsername ?? d.senderAddress ?? ''))
    const enc = String(d.smtpEncryption ?? 'STARTTLS').toUpperCase()
    if (enc === 'SSL') {
      setSmtpSsl(true)
      setSmtpStartTls(false)
    } else if (enc === 'NONE') {
      setSmtpSsl(false)
      setSmtpStartTls(false)
    } else {
      setSmtpStartTls(true)
      setSmtpSsl(false)
    }
    setSmtpPasswordConfigured(Boolean(d.smtpPasswordConfigured))
    setSmtpManagedByEnvironment(Boolean(d.smtpManagedByEnvironment))
  }, [])

  const reload = useCallback(async () => {
    setLoadError(null)
    const [platRes, aiRes, integRes, intakeRes] = await Promise.allSettled([
      api.get<{ data: Record<string, unknown> }>('/settings/platform'),
      api.get<AiStatus>('/ai/status'),
      api.get<ProviderStatus[]>('/integrations/status'),
      api.get<IntakeStatus>('/settings/intake'),
    ])
    if (platRes.status === 'fulfilled') {
      applyPlatform(platRes.value.data?.data ?? {})
    } else {
      setLoadError('Could not load integration settings.')
    }
    if (aiRes.status === 'fulfilled') setAiStatus(aiRes.value.data)
    if (integRes.status === 'fulfilled') {
      const body = integRes.value.data
      setProviders(Array.isArray(body) ? body : [])
    }
    if (intakeRes.status === 'fulfilled') setIntake(intakeRes.value.data)
  }, [applyPlatform])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        await reload()
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [reload])

  useEffect(() => {
    setTestTo(sessionEmail)
  }, [sessionEmail])

  const flag = (key: string) => Boolean(platform[key])
  const byProvider = Object.fromEntries(providers.map((row) => [row.provider, row]))

  const rows: HubRow[] = [
    {
      id: 'helios-ai',
      name: 'Helios AI',
      kind: 'Agent',
      summary: 'Anthropic Claude — extraction, chat, and agent settings',
      status: aiStatus?.blocked_reason === 'written_client_approval_required'
        ? 'blocked'
        : configuredStatus(Boolean(aiStatus?.claude_configured || flag('anthropicApiKeyConfigured'))),
      note: aiStatus?.blocked_reason === 'written_client_approval_required'
        ? 'Key present but inactive until written client approval is recorded.'
        : aiStatus?.key_source === 'environment'
          ? 'Key is set on the API host.'
          : flag('anthropicApiKeyConfigured')
            ? 'Key is saved in settings.'
            : 'Anthropic API key, model, and temperature.',
    },
    {
      id: 'maps-browser',
      name: 'Google Maps (browser)',
      kind: 'API key',
      summary: 'Maps JavaScript, in-browser geocoding, Street View',
      status: configuredStatus(flag('googleMapsApiKeyConfigured')),
      note: flag('googleMapsApiKeyConfigured')
        ? 'Referrer-restricted browser key is present.'
        : 'HTTP-referrer restricted key for Maps JS.',
    },
    {
      id: 'maps-server',
      name: 'Google Maps (server)',
      kind: 'API key',
      summary: 'Geocoding, Places photos, nearby lookups',
      status: configuredStatus(flag('googleMapsServerApiKeyConfigured')),
      note: flag('googleMapsServerApiKeyConfigured')
        ? 'Server key is present.'
        : 'IP-unrestricted server key for REST calls from the API host.',
    },
    {
      id: 'fbi-crime',
      name: 'FBI Crime Data Explorer',
      kind: 'API key',
      summary: 'State-level violent and property crime statistics',
      status: configuredStatus(flag('fbiCrimeApiKeyConfigured')),
      note: 'api.data.gov key for the Property dossier Crime & Safety block.',
    },
    {
      id: 'census',
      name: 'US Census',
      kind: 'API key',
      summary: 'ACS county population, households, age, income',
      status: configuredStatus(flag('censusApiKeyConfigured')),
      note: flag('censusApiKeyConfigured')
        ? 'Census key is present.'
        : 'Free key from api.census.gov.',
    },
    {
      id: 'regrid',
      name: 'Regrid',
      kind: 'API key',
      summary: 'Parcel boundaries on the Property map',
      status: configuredStatus(flag('regridApiKeyConfigured')),
      note: flag('regridApiKeyConfigured')
        ? 'Parcel token is present.'
        : 'Paid Regrid API token.',
    },
    {
      id: 'serper',
      name: 'Serper',
      kind: 'API key',
      summary: 'Google News / web search for CRM Contact News enrichment',
      status: configuredStatus(flag('serperApiKeyConfigured') || Boolean(byProvider.serper?.configured)),
      // A key is not consent: the note also says whether news may actually run.
      note: `${providerNote(
        byProvider.serper,
        flag('serperApiKeyConfigured')
          ? 'Serper key is present.'
          : 'Required for Re-enrich Contact News. Without a key, news is not looked up.',
      )} ${newsSignOffSummary(platform.newsSearchApproval)}`,
    },
    ...(isPermanentSuperAdmin
      ? [{
          id: 'smtp' as const,
          name: 'Outbound SMTP',
          kind: 'Email' as const,
          summary: 'ProtonMail SMTP for trial welcome and system mail',
          status: configuredStatus(
            (smtpManagedByEnvironment || smtpPasswordConfigured || Boolean(smtpPass.trim()))
            && Boolean(smtpHost.trim()),
          ),
          note: smtpManagedByEnvironment
            ? 'Controlled by SMTP_* environment variables on the API host.'
            : 'Host, port, from address, username, and password.',
        }]
      : []),
    {
      id: 'originations-mail',
      name: 'Originations mailbox',
      kind: 'Mail',
      summary: 'Graph poller and collection switches for emailed intake',
      status: intake?.collecting
        ? 'collecting'
        : intake?.mailboxConfigured
          ? 'configured'
          : intake
            ? 'not_configured'
            : 'unknown',
      note: intake?.collecting
        ? `Collecting${intake.mailbox ? ` ${intake.mailbox}` : ''}.`
        : intake?.mailboxActive && !intake.mailboxConfigured
          ? 'Collection is on, but this deployment has no mailbox credentials.'
          : 'Administrator switches for live mail collection.',
    },
    {
      id: 'costar',
      name: 'CoStar',
      kind: 'Market data',
      summary: 'Property, comps, and market statistics',
      status: configuredStatus(flag('costarApiKeyConfigured') || Boolean(byProvider.costar?.configured)),
      note: providerNote(byProvider.costar, 'CoStar API key.'),
    },
    {
      id: 'reis',
      name: "REIS / Moody's Analytics CRE",
      kind: 'Market data',
      summary: 'Metro market statistics',
      status: configuredStatus(flag('reisApiKeyConfigured') || Boolean(byProvider.reis?.configured)),
      note: providerNote(byProvider.reis, 'REIS API key.'),
    },
    {
      id: 'corelogic',
      name: 'CoreLogic',
      kind: 'Market data',
      summary: 'Property search via client credentials',
      status: configuredStatus(flag('corelogicConfigured') || Boolean(byProvider.corelogic?.configured)),
      note: providerNote(byProvider.corelogic, 'Client id and client secret.'),
    },
    {
      id: 'bloomberg',
      name: 'Bloomberg',
      kind: 'Market data',
      summary: 'Licensed rates feed',
      status: configuredStatus(flag('bloombergApiKeyConfigured') || Boolean(byProvider.bloomberg?.configured)),
      note: providerNote(byProvider.bloomberg, 'Bloomberg API key. Adapter waits on licensed credentials.'),
    },
    {
      id: 'rates-public',
      name: 'US Treasury / NY Fed',
      kind: 'Market data',
      summary: 'Public SOFR and Treasury yields',
      status: 'available',
      note: 'No credentials. The platform reads the public Treasury and NY Fed endpoints.',
    },
    {
      id: 'inbound-email',
      name: 'Originations inbound webhook',
      kind: 'Webhook',
      summary: 'Mail provider posts raw MIME into intake',
      status: configuredStatus(flag('originationsInboundTokenConfigured')),
      note: flag('originationsInboundTokenConfigured')
        ? 'ORIGINATIONS_INBOUND_TOKEN is set on the API host.'
        : 'Set ORIGINATIONS_INBOUND_TOKEN (min 32 characters) on the API host.',
    },
    {
      id: 'report-receipts',
      name: 'Report delivery receipts',
      kind: 'Webhook',
      summary: 'Provider delivery events into the report ledger',
      status: configuredStatus(flag('reportDeliveryReceiptTokenConfigured')),
      note: flag('reportDeliveryReceiptTokenConfigured')
        ? 'REPORT_DELIVERY_RECEIPT_TOKEN is set on the API host.'
        : 'Set REPORT_DELIVERY_RECEIPT_TOKEN (min 32 characters) on the API host.',
    },
  ]

  const smtpEncryptionValue = smtpSsl ? 'SSL' : smtpStartTls ? 'STARTTLS' : 'NONE'
  const smtpReadyForSend =
    Boolean(smtpHost.trim()) &&
    Boolean(smtpPort.trim()) &&
    Boolean(smtpFrom.trim()) &&
    (smtpManagedByEnvironment || Boolean(smtpPass.trim()) || smtpPasswordConfigured)

  const patchPlatform = useCallback(async (data: Record<string, unknown>, id: IntegrationId, okMessage: string) => {
    setSavingId(id)
    setFlashOk(null)
    setFlashErr(null)
    try {
      const res = await api.patch<{ data: Record<string, unknown> }>('/settings/platform', { data })
      applyPlatform(res.data?.data ?? {})
      setFlashOk(okMessage)
      await reload()
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setFlashErr(typeof detail === 'string' ? detail : 'Save failed.')
    } finally {
      setSavingId(null)
    }
  }, [applyPlatform, reload])

  const handleSmtpSave = useCallback(async () => {
    const data: Record<string, string> = {
      smtpServer: smtpHost.trim(),
      smtpPort: smtpPort.trim(),
      smtpEncryption: smtpEncryptionValue,
      senderAddress: smtpFrom.trim(),
      smtpUsername: smtpUser.trim(),
    }
    if (smtpPass.trim()) data.smtpPassword = smtpPass.trim()
    await patchPlatform(data, 'smtp', 'SMTP settings saved.')
    setSmtpPass('')
  }, [patchPlatform, smtpEncryptionValue, smtpFrom, smtpHost, smtpPass, smtpPort, smtpUser])

  const handleSmtpTest = useCallback(async () => {
    setFlashOk(null)
    setFlashErr(null)
    setSmtpTestSending(true)
    try {
      await api.post('/settings/smtp/test', { to: testTo.trim() || sessionEmail })
      setFlashOk(`Test email sent to ${testTo.trim() || sessionEmail}.`)
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setFlashErr(typeof detail === 'string' ? detail : 'Could not send test email.')
    } finally {
      setSmtpTestSending(false)
    }
  }, [sessionEmail, testTo])

  const saveSecret = (
    id: IntegrationId,
    key: string,
    value: string,
    clear: () => void,
    label: string,
  ) => {
    const data: Record<string, unknown> = {}
    if (value.trim()) data[key] = value.trim()
    void patchPlatform(data, id, `${label} saved.`).then(() => clear())
  }

  const renderFields = (id: IntegrationId) => {
    switch (id) {
      case 'helios-ai':
        return (
          <div className="space-y-4">
            <SecretField
              label="Anthropic API key"
              value={anthropicApiKey}
              onChange={setAnthropicApiKey}
              configured={flag('anthropicApiKeyConfigured')}
              placeholder="sk-ant-api03-..."
              envName="ANTHROPIC_API_KEY"
            />
            {aiStatus?.blocked_reason === 'written_client_approval_required' ? (
              <p className="text-[10px] text-amber-300">
                A configured key is a technical capability, not authorization. Production stays fail-closed until the separate approval switch is enabled after written sign-off.
              </p>
            ) : null}
            <div className="grid max-w-lg grid-cols-2 gap-x-4 gap-y-3">
              <div>
                <FormLabel>Claude model</FormLabel>
                <FormSelect
                  options={[
                    'claude-sonnet-4-20250514',
                    'claude-opus-4-20250514',
                    'claude-3-5-haiku-20241022',
                  ]}
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                />
              </div>
              <div>
                <FormLabel>Context window (reference)</FormLabel>
                <FormSelect
                  options={['32K tokens', '64K tokens', '128K tokens', '200K tokens']}
                  value={aiContextWindow}
                  onChange={(e) => setAiContextWindow(e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <FormLabel>Temperature (UI preference)</FormLabel>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={aiTempPct}
                    onChange={(e) => setAiTempPct(Number(e.target.value))}
                    className="h-1 flex-1 accent-amber-400"
                  />
                  <span className="w-8 text-right font-mono text-[12px] text-stone-300">
                    {(aiTempPct / 100).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="btn-primary text-[12px] disabled:opacity-50"
              disabled={savingId === 'helios-ai'}
              onClick={() => {
                const data: Record<string, unknown> = {
                  aiModel,
                  aiContextWindow,
                  aiTemperature: aiTempPct / 100,
                }
                if (anthropicApiKey.trim()) data.anthropicApiKey = anthropicApiKey.trim()
                void patchPlatform(data, 'helios-ai', 'Helios AI settings saved.').then(() => setAnthropicApiKey(''))
              }}
            >
              {savingId === 'helios-ai' ? 'Saving…' : 'Save agent settings'}
            </button>
          </div>
        )
      case 'maps-browser':
        return (
          <div className="space-y-3">
            <SecretField
              label="Google Maps browser key"
              value={googleMapsApiKey}
              onChange={setGoogleMapsApiKey}
              configured={flag('googleMapsApiKeyConfigured')}
              placeholder="AIza…"
              envName="GOOGLE_MAPS_API_KEY"
            />
            <p className="max-w-lg text-[10px] leading-relaxed text-stone-600">
              Enable Maps JavaScript API, Geocoding API, and Places API (New). Restrict by HTTP referrer to this site&apos;s origin.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="btn-primary text-[12px] disabled:opacity-50"
                disabled={savingId === 'maps-browser'}
                onClick={() => saveSecret('maps-browser', 'googleMapsApiKey', googleMapsApiKey, () => setGoogleMapsApiKey(''), 'Browser Maps key')}
              >
                {savingId === 'maps-browser' ? 'Saving…' : 'Save browser key'}
              </button>
              <button
                type="button"
                disabled={browserProbing || !flag('googleMapsApiKeyConfigured')}
                className="text-[11px] text-amber-300 hover:text-amber-200 disabled:opacity-40"
                onClick={async () => {
                  setBrowserProbing(true)
                  try {
                    const config = await mapsConfigService.get()
                    if (!config.apiKey) {
                      setBrowserChecks([{ api: 'Browser Maps key', ok: false, message: 'No browser key is saved.', remedy: null }])
                      return
                    }
                    setBrowserChecks(await probeBrowserMaps(config.apiKey))
                  } catch (error) {
                    setBrowserChecks([{
                      api: 'Browser Maps key',
                      ok: false,
                      message: error instanceof Error ? error.message : 'The browser test failed.',
                      remedy: null,
                    }])
                  } finally {
                    setBrowserProbing(false)
                  }
                }}
              >
                {browserProbing ? 'Testing in this browser…' : 'Test browser key (this page)'}
              </button>
            </div>
            {browserChecks?.map((check) => (
              <p key={check.api} className={check.ok ? 'text-[10px] text-stone-400' : 'text-[10px] text-red-300'}>
                {check.api}: {check.message}
              </p>
            ))}
          </div>
        )
      case 'maps-server':
        return (
          <div className="space-y-3">
            <SecretField
              label="Google Maps server key"
              value={googleMapsServerApiKey}
              onChange={setGoogleMapsServerApiKey}
              configured={flag('googleMapsServerApiKeyConfigured')}
              placeholder="AIza… (API-restricted — API host only)"
              envName="GOOGLE_MAPS_SERVER_API_KEY"
            />
            <p className="max-w-lg text-[10px] leading-relaxed text-stone-600">
              Needs Geocoding API, Places API (New), and Street View Static API. A referrer-restricted browser key cannot make these calls.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="btn-primary text-[12px] disabled:opacity-50"
                disabled={savingId === 'maps-server'}
                onClick={() => saveSecret('maps-server', 'googleMapsServerApiKey', googleMapsServerApiKey, () => setGoogleMapsServerApiKey(''), 'Server Maps key')}
              >
                {savingId === 'maps-server' ? 'Saving…' : 'Save server key'}
              </button>
              <button
                type="button"
                disabled={mapsProbing || (!flag('googleMapsApiKeyConfigured') && !flag('googleMapsServerApiKeyConfigured'))}
                className="text-[11px] text-amber-300 hover:text-amber-200 disabled:opacity-40"
                onClick={async () => {
                  setMapsProbing(true)
                  try {
                    const probed = await mapsConfigService.probe()
                    setMapsProbe(probed.probe ?? null)
                    await queryClient.invalidateQueries({ queryKey: ['settings', 'maps-config'] })
                  } catch {
                    setMapsProbe({ ok: false, error: 'Could not reach the Maps settings endpoint.' })
                  } finally {
                    setMapsProbing(false)
                  }
                }}
              >
                {mapsProbing ? 'Testing saved keys…' : 'Test saved keys (server)'}
              </button>
            </div>
            {mapsProbe?.error ? <p className="text-[10px] text-red-300">{mapsProbe.error}</p> : null}
            {mapsProbe?.server?.message ? (
              <p className={`text-[10px] ${mapsProbe.server.ok ? 'text-emerald-400' : 'text-red-300'}`}>
                {mapsProbe.server.message}
              </p>
            ) : null}
          </div>
        )
      case 'fbi-crime':
        return (
          <div className="space-y-3">
            <SecretField
              label="FBI Crime Data Explorer API key"
              value={fbiCrimeApiKey}
              onChange={setFbiCrimeApiKey}
              configured={flag('fbiCrimeApiKeyConfigured')}
              placeholder="Enter your api.data.gov key"
              envName="FBI_CRIME_API_KEY"
            />
            <button
              type="button"
              className="btn-primary text-[12px] disabled:opacity-50"
              disabled={savingId === 'fbi-crime'}
              onClick={() => saveSecret('fbi-crime', 'fbiCrimeApiKey', fbiCrimeApiKey, () => setFbiCrimeApiKey(''), 'FBI Crime key')}
            >
              {savingId === 'fbi-crime' ? 'Saving…' : 'Save key'}
            </button>
          </div>
        )
      case 'census':
        return (
          <div className="space-y-3">
            <SecretField
              label="US Census API key"
              value={censusApiKey}
              onChange={setCensusApiKey}
              configured={flag('censusApiKeyConfigured')}
              placeholder="40-character key from api.census.gov"
              envName="CENSUS_API_KEY"
            />
            <button
              type="button"
              className="btn-primary text-[12px] disabled:opacity-50"
              disabled={savingId === 'census'}
              onClick={() => saveSecret('census', 'censusApiKey', censusApiKey, () => setCensusApiKey(''), 'Census key')}
            >
              {savingId === 'census' ? 'Saving…' : 'Save key'}
            </button>
          </div>
        )
      case 'regrid':
        return (
          <div className="space-y-3">
            <SecretField
              label="Regrid parcel API token"
              value={regridApiKey}
              onChange={setRegridApiKey}
              configured={flag('regridApiKeyConfigured')}
              placeholder="API token from app.regrid.com"
              envName="REGRID_API_KEY"
            />
            <button
              type="button"
              className="btn-primary text-[12px] disabled:opacity-50"
              disabled={savingId === 'regrid'}
              onClick={() => saveSecret('regrid', 'regridApiKey', regridApiKey, () => setRegridApiKey(''), 'Regrid token')}
            >
              {savingId === 'regrid' ? 'Saving…' : 'Save token'}
            </button>
          </div>
        )
      case 'serper':
        return (
          <div className="space-y-3">
            <SecretField
              label="Serper API key"
              value={serperApiKey}
              onChange={setSerperApiKey}
              configured={flag('serperApiKeyConfigured')}
              placeholder="Serper key for Contact News"
              envName="SERPER_API_KEY"
            />
            <p className="max-w-lg text-[10px] leading-relaxed text-stone-600">
              Operator path: Settings → Integrations → Serper. Contact News reads this saved key or{' '}
              <span className="font-mono">SERPER_API_KEY</span> on the API host. Unconfigured looks
              unconfigured — articles are never invented.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="btn-primary text-[12px] disabled:opacity-50"
                disabled={savingId === 'serper'}
                onClick={() => saveSecret('serper', 'serperApiKey', serperApiKey, () => setSerperApiKey(''), 'Serper key')}
              >
                {savingId === 'serper' ? 'Saving…' : 'Save key'}
              </button>
              {flag('serperApiKeyConfigured') ? (
                <button
                  type="button"
                  className="text-[11px] text-stone-400 hover:text-stone-200 disabled:opacity-40"
                  disabled={savingId === 'serper'}
                  onClick={() => {
                    void patchPlatform({ clearKeys: ['serperApiKey'] }, 'serper', 'Saved Serper key removed.')
                      .then(() => setSerperApiKey(''))
                  }}
                >
                  Remove saved key
                </button>
              ) : null}
            </div>
            <NewsSignOffPanel
              approval={platform.newsSearchApproval}
              onChanged={(next) => setPlatform((current) => ({ ...current, newsSearchApproval: next }))}
            />
          </div>
        )
      case 'smtp':
        return (
          <div className="space-y-3">
            {smtpManagedByEnvironment ? (
              <div className="flex items-center gap-2 rounded-lg border border-amber-400/25 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100/90">
                <Mail size={14} className="shrink-0 text-amber-400" />
                Outbound SMTP is controlled by environment variables on the API server. Send a test email below.
              </div>
            ) : null}
            <div className="grid max-w-2xl grid-cols-2 gap-x-4 gap-y-3">
              <div>
                <FormLabel>SMTP host</FormLabel>
                <FormInput disabled={smtpManagedByEnvironment} value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="SMTP host name" />
              </div>
              <div>
                <FormLabel>Port</FormLabel>
                <FormInput disabled={smtpManagedByEnvironment} value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} placeholder="587" />
              </div>
              <div>
                <FormLabel>From address</FormLabel>
                <FormInput disabled={smtpManagedByEnvironment} value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)} placeholder="Sender mailbox address" />
              </div>
              <div>
                <FormLabel>SMTP username</FormLabel>
                <FormInput disabled={smtpManagedByEnvironment} value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} placeholder="Usually the sender address" />
              </div>
              <div className="col-span-2">
                <FormLabel>SMTP password</FormLabel>
                <FormInput
                  disabled={smtpManagedByEnvironment}
                  type="password"
                  value={smtpPass}
                  onChange={(e) => setSmtpPass(e.target.value)}
                  placeholder={smtpManagedByEnvironment ? 'Set via API environment' : smtpPasswordConfigured ? '••••••••' : 'Proton SMTP token or password'}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="flex cursor-pointer items-center gap-2 text-[11px] text-stone-300">
                <input
                  checked={smtpStartTls}
                  className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400"
                  disabled={smtpManagedByEnvironment}
                  onChange={(e) => {
                    const on = e.target.checked
                    setSmtpStartTls(on)
                    if (on) setSmtpSsl(false)
                  }}
                  type="checkbox"
                />
                STARTTLS (port 587)
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-[11px] text-stone-300">
                <input
                  checked={smtpSsl}
                  className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400"
                  disabled={smtpManagedByEnvironment}
                  onChange={(e) => {
                    const on = e.target.checked
                    setSmtpSsl(on)
                    if (on) {
                      setSmtpStartTls(false)
                      setSmtpPort((p) => (p === '587' || !p ? '465' : p))
                    }
                  }}
                  type="checkbox"
                />
                SSL/TLS (port 465)
              </label>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <button
                className="btn-primary flex items-center justify-center gap-2 text-[12px]"
                disabled={savingId === 'smtp' || smtpManagedByEnvironment}
                onClick={() => void handleSmtpSave()}
                type="button"
              >
                <CheckCircle2 size={14} /> {savingId === 'smtp' ? 'Saving…' : 'Save SMTP settings'}
              </button>
              <div className="min-w-[200px] flex-1">
                <FormLabel>Send test to</FormLabel>
                <FormInput value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@company.com" />
              </div>
              <button
                className="btn-secondary flex items-center justify-center gap-2 text-[12px]"
                disabled={smtpTestSending || !smtpReadyForSend}
                onClick={() => void handleSmtpTest()}
                type="button"
              >
                <Send size={14} /> {smtpTestSending ? 'Sending…' : 'Send test email'}
              </button>
            </div>
          </div>
        )
      case 'originations-mail':
        return <IntakeMailSettings />
      case 'costar':
        return (
          <div className="space-y-3">
            <SecretField
              label="CoStar API key"
              value={costarApiKey}
              onChange={setCostarApiKey}
              configured={flag('costarApiKeyConfigured')}
              placeholder="CoStar bearer token"
              envName="COSTAR_API_KEY"
            />
            <button
              type="button"
              className="btn-primary text-[12px] disabled:opacity-50"
              disabled={savingId === 'costar'}
              onClick={() => saveSecret('costar', 'costarApiKey', costarApiKey, () => setCostarApiKey(''), 'CoStar key')}
            >
              {savingId === 'costar' ? 'Saving…' : 'Save key'}
            </button>
          </div>
        )
      case 'reis':
        return (
          <div className="space-y-3">
            <SecretField
              label="REIS API key"
              value={reisApiKey}
              onChange={setReisApiKey}
              configured={flag('reisApiKeyConfigured')}
              placeholder="REIS / Moody's Analytics CRE key"
              envName="REIS_API_KEY"
            />
            <button
              type="button"
              className="btn-primary text-[12px] disabled:opacity-50"
              disabled={savingId === 'reis'}
              onClick={() => saveSecret('reis', 'reisApiKey', reisApiKey, () => setReisApiKey(''), 'REIS key')}
            >
              {savingId === 'reis' ? 'Saving…' : 'Save key'}
            </button>
          </div>
        )
      case 'corelogic':
        return (
          <div className="space-y-3">
            <SecretField
              label="CoreLogic client id"
              value={corelogicClientId}
              onChange={setCorelogicClientId}
              configured={flag('corelogicConfigured')}
              placeholder="OAuth client id"
              envName="CORELOGIC_CLIENT_ID"
            />
            <SecretField
              label="CoreLogic client secret"
              value={corelogicClientSecret}
              onChange={setCorelogicClientSecret}
              configured={flag('corelogicConfigured')}
              placeholder="OAuth client secret"
              envName="CORELOGIC_CLIENT_SECRET"
            />
            <button
              type="button"
              className="btn-primary text-[12px] disabled:opacity-50"
              disabled={savingId === 'corelogic'}
              onClick={() => {
                const data: Record<string, unknown> = {}
                if (corelogicClientId.trim()) data.corelogicClientId = corelogicClientId.trim()
                if (corelogicClientSecret.trim()) data.corelogicClientSecret = corelogicClientSecret.trim()
                void patchPlatform(data, 'corelogic', 'CoreLogic credentials saved.').then(() => {
                  setCorelogicClientId('')
                  setCorelogicClientSecret('')
                })
              }}
            >
              {savingId === 'corelogic' ? 'Saving…' : 'Save credentials'}
            </button>
          </div>
        )
      case 'bloomberg':
        return (
          <div className="space-y-3">
            <SecretField
              label="Bloomberg API key"
              value={bloombergApiKey}
              onChange={setBloombergApiKey}
              configured={flag('bloombergApiKeyConfigured')}
              placeholder="Bloomberg licensed key"
              envName="BLOOMBERG_API_KEY"
            />
            <p className="max-w-lg text-[10px] text-stone-600">
              The adapter records that a licensed B-PIPE connection is required. Saving a key marks the integration configured; live quotes still wait on that licensed wiring.
            </p>
            <button
              type="button"
              className="btn-primary text-[12px] disabled:opacity-50"
              disabled={savingId === 'bloomberg'}
              onClick={() => saveSecret('bloomberg', 'bloombergApiKey', bloombergApiKey, () => setBloombergApiKey(''), 'Bloomberg key')}
            >
              {savingId === 'bloomberg' ? 'Saving…' : 'Save key'}
            </button>
          </div>
        )
      case 'rates-public':
        return (
          <p className="max-w-lg text-[11px] leading-relaxed text-stone-400">
            No fields to populate. The platform reads SOFR from markets.newyorkfed.org and Treasury yields from fiscaldata.treasury.gov.
          </p>
        )
      case 'inbound-email':
        return (
          <div className="max-w-xl space-y-2 text-[11px] text-stone-400">
            <p>The mail provider posts raw MIME to this inbound route. There is no outbound webhook store.</p>
            <p className="font-mono text-[10px] text-stone-300">POST /api/v1/ingestion/inbound-email</p>
            <p>Header <span className="font-mono text-stone-300">X-Originations-Token</span></p>
            <p>
              Token environment variable <span className="font-mono text-stone-300">ORIGINATIONS_INBOUND_TOKEN</span>
              {' '}(minimum 32 characters). The token is not stored from this screen.
            </p>
            <p>
              Status:{' '}
              {flag('originationsInboundTokenConfigured')
                ? 'token is present on the API host'
                : 'token is not configured — the route fails closed with 503'}
            </p>
          </div>
        )
      case 'report-receipts':
        return (
          <div className="max-w-xl space-y-2 text-[11px] text-stone-400">
            <p>Report providers post delivery receipts to the ledger. There is no outbound webhook store.</p>
            <p className="font-mono text-[10px] text-stone-300">POST /api/v1/report-runtime/deliveries/events</p>
            <p className="font-mono text-[10px] text-stone-300">POST /api/v1/report-runtime/deliveries/{'{attempt_id}'}/events</p>
            <p>Header <span className="font-mono text-stone-300">X-Report-Delivery-Token</span></p>
            <p>
              Token environment variable <span className="font-mono text-stone-300">REPORT_DELIVERY_RECEIPT_TOKEN</span>
              {' '}(minimum 32 characters). The token is not stored from this screen.
            </p>
            <p>
              Status:{' '}
              {flag('reportDeliveryReceiptTokenConfigured')
                ? 'token is present on the API host'
                : 'token is not configured — the route fails closed with 503'}
            </p>
          </div>
        )
      default: {
        const _exhaustive: never = id
        return _exhaustive
      }
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-1 text-[13px] font-semibold text-stone-100">Integrations</h3>
        <p className="max-w-3xl text-[11px] leading-relaxed text-stone-500">
          Every connection the platform actually talks to. Click a row to enter the fields that row needs.
          API keys, agent settings, and inbound webhooks live in this list.
        </p>
      </div>
      {loadError ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          <AlertCircle size={14} /> {loadError}
        </div>
      ) : null}
      <Flash ok={flashOk} err={flashErr} />
      {loading ? <div className="text-[12px] text-stone-500">Loading integrations…</div> : null}
      <div className="overflow-hidden rounded-lg border border-amber-400/10 bg-black/30">
        <table className="dense-table">
          <thead>
            <tr>
              <th>Integration</th>
              <th>Type</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const open = openId === row.id
              return (
                <Fragment key={row.id}>
                  <tr
                    className="cursor-pointer"
                    aria-expanded={open}
                    onClick={() => setOpenId(open ? null : row.id)}
                  >
                    <td className="font-medium text-stone-200">
                      <span className="inline-flex items-center gap-2">
                        <ChevronDown
                          size={12}
                          className={`text-stone-500 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`}
                        />
                        {row.name}
                      </span>
                    </td>
                    <td className="text-[11px] text-stone-400">{row.kind}</td>
                    <td><StatusBadge status={row.status} /></td>
                    <td className="text-[11px] text-stone-500">{row.note}</td>
                  </tr>
                  {open ? (
                    <tr className="!bg-black/40">
                      <td colSpan={4} className="!py-4">
                        <p className="mb-3 text-[11px] text-stone-500">{row.summary}</p>
                        {renderFields(row.id)}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

